import {Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import {
  FindControlAdditionalSearchParametersDto,
  FindControlInitDto,
  FindControlRequiredSearchParametersDto,
  FindControlResultDto,
  HiddenActionItemType,
  IColumnPk,
  IDeleted,
  ISelected,
} from 'webcommon/shared';
import { ApFindControlDeleteService } from './delete/ap-findcontrol-delete.service';
import { FindControlDeleteAction } from './delete/DeleteFindControlItemOperation';
import { FindControlMode } from '../models/index';
import {
  ApComplexModal,
  ApNotificationService,
  ApUndoableOperationService,
} from 'webcommon/core-ui';
import { FcItemModalService } from './generic/fc-item-modal.service';
import { FcItemModalMode, FcModifyModal, FcNewModal } from '../models/FcItemModalContext';
import { ApFindControlAdvancedUpdate, ApFindControlAdvancedUpdateAction } from './ap-findcontrol-advanced.component';

interface FindControlChip {
  Id: string;
  Item: IColumnPk & ISelected & IDeleted;
}

@Component({
  selector: 'ap-findcontrol-modal',
  templateUrl: './ap-findcontrol-modal.component.html',
  styleUrls: ['./ap-findcontrol-modal.component.less'],
})
export class ApFindControlModalComponent extends ApComplexModal<IColumnPk[]> implements OnInit, OnDestroy {
  // initial state params
  baseId: string;
  preselectedItems: IColumnPk[] = [];
  findControlId: string;
  findControlDisplayName: string;
  Id: string;
  requiredSearchParameters?: FindControlRequiredSearchParametersDto;
  additionalSearchParameters?: FindControlAdditionalSearchParametersDto;
  mode: FindControlMode;
  FindControlMode = FindControlMode;
  hiddenActionItems?: HiddenActionItemType[];
  chipsExpanded = false;

  // UI properties
  chips: FindControlChip[] = [];
  dirty = false;
  findControlInitDto: FindControlInitDto;
  loadingConfig = true;
  loadingData = true;
  protected readonly updateObs$ = new Subject<ApFindControlAdvancedUpdate>();
  readonly updateObs: Observable<ApFindControlAdvancedUpdate> = this.updateObs$.asObservable();

  undoComponentId: number;

  constructor(
    elementInjector: Injector,
    protected apNotificationService: ApNotificationService,
    private apFindControlDeleteService: ApFindControlDeleteService,
    protected apUndoableOperationService: ApUndoableOperationService,
    private fcItemModalService: FcItemModalService,
  ) {
    super(elementInjector);
  }

  override ngOnInit() {
    super.ngOnInit();
    this.chips = this.preselectedItems.map(item => ({ Id: item.Pk, Item: item }));
    this.undoComponentId = this.apUndoableOperationService.registerComponent();
  }

  override ngOnDestroy(): void {
    this.apUndoableOperationService.unregisterComponent(this.undoComponentId);
    super.ngOnDestroy();
  }

  onFindControlLoaded(findControlInitDto: FindControlInitDto) {
    this.findControlInitDto = findControlInitDto;
    this.loadingConfig = false;
  }

  onFindControlDataLoaded(resultDto: FindControlResultDto) {
    this.updateSelectedRows(resultDto.Rows);
    this.loadingData = false;
  }

  private updateSelectedRows(data: IColumnPk[]) {
    this.chips.forEach((chip) => {
      const result = data.find((dataItem) => dataItem.Pk === chip.Id);
      if (result) {
        (result as ISelected).Selected = true;
        chip.Item = result;
      }
    });
  }

  close() {
    this.apUndoableOperationService.queueOperation(this.undoComponentId, () => {
      super.apClose(this.chips.map(chip => chip.Item));
      return {
        completionPromise: Promise.resolve(),
      };
    });
  }

  cancel() {
    this.apUndoableOperationService.queueOperation(this.undoComponentId, () => {
      super.apUserCancel();
      return {
        completionPromise: Promise.resolve(),
      };
    });
  }

  onNewClick() {
    this.apUndoableOperationService.queueOperation(this.undoComponentId, () => {
      const context: FcNewModal = {
        mode: FcItemModalMode.New,
        findControlId: this.findControlInitDto.FindControlBehavior.Id,
        findControlName: this.findControlInitDto.FindControlBehavior.Name,
      };
      const promise = this.fcItemModalService.openModal(context).then(result => {
        if (result.isSuccess) {
          this.updateObs$.next({
            action: ApFindControlAdvancedUpdateAction.New,
            dataItem: result.resultValue,
          });
        }
      });
      return {
        completionPromise: promise,
      };
    });
  }

  private onDirtyChanged() {
    this.dirty = true;
  }

  onHyperLinkSelect(dataDto: IColumnPk & ISelected) {
    dataDto.Selected = true;
    this.onSelectChange(dataDto);
    this.close();
  }

  onSelectChange(dataItem: ISelected & IColumnPk) {
    let changed = false;
    if (dataItem.Selected) {
      changed = this.addChip(dataItem);
    } else {
      changed = this.removeChip(dataItem);
    }

    if (changed) {
      this.onDirtyChanged();
    }
  }

  onDelete(dataItem: IColumnPk & IDeleted) {
    this.apFindControlDeleteService.deleteItem(
      this.undoComponentId,
      this.findControlInitDto.FindControlBehavior.Id,
      this.findControlInitDto.FindControlBehavior.Name,
      this.findControlInitDto.FindControlBehavior.ColumnsConfig.PrimaryKey,
      dataItem
    ).pipe(
      this.takeUntilDestroy(),
    ).subscribe((checkpoint) => {
      switch (checkpoint.action) {
        case FindControlDeleteAction.Predelete: {
          this.removeChip(dataItem);
          this.updateObs$.next({
            action: ApFindControlAdvancedUpdateAction.Hide,
            dataItem,
          });
          break;
        }
        case FindControlDeleteAction.DeleteFailure:
        case FindControlDeleteAction.Undo: {
          this.updateObs$.next({
            action: ApFindControlAdvancedUpdateAction.Show,
            dataItem,
          });
          break;
        }
      }
    });
  }

  onModify(dataItem: IColumnPk & ISelected) {
    this.apUndoableOperationService.queueOperation(this.undoComponentId, () => {
      const context: FcModifyModal = {
        mode: FcItemModalMode.Modify,
        findControlId: this.findControlInitDto.FindControlBehavior.Id,
        findControlName: this.findControlInitDto.FindControlBehavior.Name,
        selectedFindItemPk: dataItem.Pk,
        selectedFindItemName: dataItem.Name,

      };
      const promise = this.fcItemModalService.openModal(context).then(result => {
        if (result.isSuccess) {
          this.updateObs$.next({
            action: ApFindControlAdvancedUpdateAction.Modify,
            dataItem: result.resultValue,
          });
        }
      });
      return {
        completionPromise: promise,
      };
    });
  }

  private addChip(object: IColumnPk & ISelected): boolean {
    object.Selected = true;
    const newChip = {
      Id: object.Pk,
      Item: object,
    };

    if (this.mode === FindControlMode.SingleSelect && this.chips.length) {
      if (this.chips[0].Id !== newChip.Id) {
        this.chips = [ newChip ];
        return true;
      } else {
        return false;
      }
    }

    const index = this.chips.map((chip) => chip.Id).indexOf(newChip.Id);

    if (index === -1) {
      this.chips.push(newChip);
      return true;
    } else {
      this.chips[index] = newChip;
      return false;
    }
  }

  onChipDelete(chip: FindControlChip) { // This is when performed from ui - need to uncheck the corresponding checkbox
    this.removeChip(chip.Item);
  }

  private removeChip(object: IColumnPk & ISelected): boolean {
    object.Selected = false;
    const index = this.chips.map((chip) => chip.Id).indexOf(object.Pk);

    if (index >= 0) {
      this.chips.splice(index, 1);
      this.onDirtyChanged();
      return true;
    }
    return false;
  }
  toggleChips() {
    this.chipsExpanded = !this.chipsExpanded;
  }
}
